export const WANX_VIDEO_PROVIDER = 'wanx';
export const WANX_VIDEO_MODEL = 'wan3.0-video';
export const WANX_VIDEO_PRIME_MODEL = 'wan3.0-video-prime';
export const WANX_VIDEO_MODELS = Object.freeze([
  Object.freeze({ value: WANX_VIDEO_MODEL, label: '标准版', description: '万相 3.0 标准版' }),
  Object.freeze({ value: WANX_VIDEO_PRIME_MODEL, label: '高速版', description: '能力对齐标准版，生成速度更快' }),
]);

export const WANX_VIDEO_RESOLUTIONS = Object.freeze(['480P', '720P', '1080P']);
export const WANX_VIDEO_RATIOS = Object.freeze(['adaptive', '16:9', '4:3', '1:1', '3:4', '9:16']);
export const WANX_VIDEO_DURATION_OPTIONS = Object.freeze([
  { value: '-1', label: '智能时长' },
  ...Array.from({ length: 29 }, (_, index) => `${index + 2}s`),
]);

export const WANX_VIDEO_MEDIA_TYPES = Object.freeze({
  FIRST_FRAME: 'first_frame',
  LAST_FRAME: 'last_frame',
  REFERENCE_IMAGE: 'reference_image',
  REFERENCE_VIDEO: 'reference_video',
  REFERENCE_AUDIO: 'reference_audio',
  FILE: 'file',
  LINK: 'link',
});

export const WANX_VIDEO_ACCEPT = Object.freeze({
  image: '.jpg,.jpeg,.png,.bmp,.webp,image/jpeg,image/png,image/bmp,image/webp',
  video: '.mp4,.mov,video/mp4,video/quicktime',
  audio: '.wav,.mp3,audio/wav,audio/x-wav,audio/mpeg',
  file: '.docx,.doc,.xlsx,.xls,.pptx,.ppt,.pdf,.txt,.key,.pages,.numbers,.md',
});

const FRAME_TYPES = new Set([
  WANX_VIDEO_MEDIA_TYPES.FIRST_FRAME,
  WANX_VIDEO_MEDIA_TYPES.LAST_FRAME,
]);
const REFERENCE_TYPES = new Set([
  WANX_VIDEO_MEDIA_TYPES.REFERENCE_IMAGE,
  WANX_VIDEO_MEDIA_TYPES.REFERENCE_VIDEO,
  WANX_VIDEO_MEDIA_TYPES.REFERENCE_AUDIO,
]);
const ALLOWED_TYPES = new Set([
  ...FRAME_TYPES,
  ...REFERENCE_TYPES,
  WANX_VIDEO_MEDIA_TYPES.FILE,
  WANX_VIDEO_MEDIA_TYPES.LINK,
]);

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'bmp', 'webp']);
const VIDEO_EXTENSIONS = new Set(['mp4', 'mov']);
const AUDIO_EXTENSIONS = new Set(['wav', 'mp3']);
const FILE_EXTENSIONS = new Set(['docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt', 'pdf', 'txt', 'key', 'pages', 'numbers', 'md']);
const IMAGE_TYPES = new Set([
  WANX_VIDEO_MEDIA_TYPES.FIRST_FRAME,
  WANX_VIDEO_MEDIA_TYPES.LAST_FRAME,
  WANX_VIDEO_MEDIA_TYPES.REFERENCE_IMAGE,
]);

const MB = 1024 * 1024;
const getExtension = (name = '') => String(name).split('.').pop()?.toLowerCase() || '';
const finiteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const isWanxVideoProvider = (provider) => (
  String(provider || '').trim().toLowerCase() === WANX_VIDEO_PROVIDER
);

export const isWanxVideoModel = (model) => (
  WANX_VIDEO_MODELS.some(({ value }) => value === String(model || '').trim().toLowerCase())
);

export const normalizeWanxVideoModel = (model, fallback = WANX_VIDEO_MODEL) => {
  const normalized = String(model || '').trim().toLowerCase();
  if (isWanxVideoModel(normalized)) return normalized;
  const normalizedFallback = String(fallback || '').trim().toLowerCase();
  return isWanxVideoModel(normalizedFallback) ? normalizedFallback : WANX_VIDEO_MODEL;
};

export const normalizeWanxVideoResolution = (resolution, fallback = '1080P') => {
  const normalized = String(resolution || '').trim().toUpperCase();
  if (WANX_VIDEO_RESOLUTIONS.includes(normalized)) return normalized;
  const normalizedFallback = String(fallback || '').trim().toUpperCase();
  return WANX_VIDEO_RESOLUTIONS.includes(normalizedFallback) ? normalizedFallback : '1080P';
};

export const normalizeWanxVideoRatio = (ratio, fallback = 'adaptive') => {
  const normalized = String(ratio || '').trim().toLowerCase();
  if (WANX_VIDEO_RATIOS.includes(normalized)) return normalized;
  return WANX_VIDEO_RATIOS.includes(fallback) ? fallback : 'adaptive';
};

export const normalizeWanxVideoDuration = (duration, fallback = 5) => {
  const raw = typeof duration === 'string' ? duration.replace(/s$/i, '').trim() : duration;
  const parsed = Number(raw);
  if (parsed === -1) return -1;
  if (Number.isInteger(parsed) && parsed >= 2 && parsed <= 30) return parsed;
  return Number.isInteger(fallback) && fallback >= 2 && fallback <= 30 ? fallback : 5;
};

export const normalizeWanxVideoSeed = (seed) => {
  if (seed === '' || seed == null) return null;
  const parsed = Number(seed);
  if (!Number.isInteger(parsed) || parsed < 0 || parsed > 2147483647) {
    throw new Error('随机种子必须是 0 至 2147483647 的整数');
  }
  return parsed;
};

export const normalizeWanxVideoMedia = (media) => {
  if (!Array.isArray(media)) return [];
  return media.reduce((result, item, index) => {
    const type = String(item?.type || '').trim().toLowerCase();
    const url = String(item?.url || '').trim();
    if (!ALLOWED_TYPES.has(type) || !url) return result;
    result.push({
      id: String(item.id || `wanx-media-${Date.now()}-${index}`),
      type,
      url,
      name: String(item.name || '').trim(),
      ...(finiteNumber(item.duration ?? item.durationSeconds) != null
        ? { duration: finiteNumber(item.duration ?? item.durationSeconds) }
        : {}),
      ...(finiteNumber(item.width) != null ? { width: finiteNumber(item.width) } : {}),
      ...(finiteNumber(item.height) != null ? { height: finiteNumber(item.height) } : {}),
    });
    return result;
  }, []);
};

export const createWanxVideoMediaItem = ({ type, url, name = '', duration, width, height }) => {
  const normalized = normalizeWanxVideoMedia([{
    id: `wanx-media-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type,
    url,
    name,
    duration,
    width,
    height,
  }]);
  if (!normalized.length) throw new Error('无效的万相参考素材');
  return normalized[0];
};

const assertDimensions = (width, height, minSide, maxSide, label) => {
  if (!width || !height) throw new Error(`无法读取${label}尺寸`);
  if (width < minSide || height < minSide || width > maxSide || height > maxSide) {
    throw new Error(`${label}单边尺寸需在 ${minSide}-${maxSide}px 之间`);
  }
  if (Math.max(width / height, height / width) > 8) {
    throw new Error(`${label}长宽比不能超过 8:1`);
  }
};

const readImageMetadata = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const image = new Image();
  image.onload = () => {
    const width = image.naturalWidth;
    const height = image.naturalHeight;
    let hasAlpha = false;
    if (getExtension(file.name) === 'png') {
      try {
        const scale = Math.min(1, 256 / Math.max(width, height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(width * scale));
        canvas.height = Math.max(1, Math.round(height * scale));
        const context = canvas.getContext('2d', { willReadFrequently: true });
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
        for (let index = 3; index < pixels.length; index += 4) {
          if (pixels[index] < 255) {
            hasAlpha = true;
            break;
          }
        }
      } catch {
        // 浏览器无法读取像素时交由服务端做最终透明通道校验。
      }
    }
    URL.revokeObjectURL(url);
    resolve({ width, height, hasAlpha });
  };
  image.onerror = () => {
    URL.revokeObjectURL(url);
    reject(new Error('无法读取图片，请确认文件未损坏'));
  };
  image.src = url;
});

const readTimedMediaMetadata = (file, kind) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const element = document.createElement(kind === 'video' ? 'video' : 'audio');
  element.preload = 'metadata';
  element.onloadedmetadata = () => {
    const metadata = {
      duration: Number(element.duration),
      ...(kind === 'video' ? { width: element.videoWidth, height: element.videoHeight } : {}),
    };
    URL.revokeObjectURL(url);
    resolve(metadata);
  };
  element.onerror = () => {
    URL.revokeObjectURL(url);
    reject(new Error(`无法读取${kind === 'video' ? '视频' : '音频'}，请确认文件未损坏`));
  };
  element.src = url;
});

export const validateWanxVideoMediaFile = async (file, mediaType) => {
  if (typeof File === 'undefined' || !(file instanceof File)) throw new Error('请选择有效文件');
  const type = String(mediaType || '').trim().toLowerCase();
  const extension = getExtension(file.name);

  if (IMAGE_TYPES.has(type)) {
    if (!IMAGE_EXTENSIONS.has(extension) || file.size > 20 * MB) {
      throw new Error('图片仅支持 JPG/JPEG/PNG/BMP/WEBP，且不能超过 20MB');
    }
    const metadata = await readImageMetadata(file);
    assertDimensions(metadata.width, metadata.height, 240, 8000, '图片');
    if (metadata.hasAlpha) throw new Error('PNG 图片不能包含透明通道');
    return metadata;
  }

  if (type === WANX_VIDEO_MEDIA_TYPES.REFERENCE_VIDEO) {
    if (!VIDEO_EXTENSIONS.has(extension) || file.size > 100 * MB) {
      throw new Error('参考视频仅支持 MP4/MOV，且不能超过 100MB');
    }
    const metadata = await readTimedMediaMetadata(file, 'video');
    assertDimensions(metadata.width, metadata.height, 240, 4096, '视频');
    if (!Number.isFinite(metadata.duration)) throw new Error('无法读取视频时长');
    if (metadata.duration < 1 || metadata.duration > 15) throw new Error('单个参考视频时长需在 1-15 秒之间');
    return metadata;
  }

  if (type === WANX_VIDEO_MEDIA_TYPES.REFERENCE_AUDIO) {
    if (!AUDIO_EXTENSIONS.has(extension) || file.size > 15 * MB) {
      throw new Error('参考音频仅支持 WAV/MP3，且不能超过 15MB');
    }
    const metadata = await readTimedMediaMetadata(file, 'audio');
    if (!Number.isFinite(metadata.duration)) throw new Error('无法读取音频时长');
    if (metadata.duration < 1 || metadata.duration > 15) throw new Error('单个参考音频时长需在 1-15 秒之间');
    return metadata;
  }

  if (type === WANX_VIDEO_MEDIA_TYPES.FILE) {
    if (!FILE_EXTENSIONS.has(extension) || file.size > 100 * MB) {
      throw new Error('参考文件格式不支持或超过 100MB');
    }
    return {};
  }

  throw new Error('当前素材类型不支持本地上传');
};

export const inferWanxVideoReferenceType = (item = {}, url = '') => {
  const cleanUrl = String(url || item.url || '').split('?')[0].split('#')[0];
  const extension = getExtension(cleanUrl);
  const typeText = `${item.category || ''} ${item.typeCode || ''} ${item.type || ''} ${item.resourceType || ''}`.toLowerCase();
  if (typeText.includes('video') || VIDEO_EXTENSIONS.has(extension)) return WANX_VIDEO_MEDIA_TYPES.REFERENCE_VIDEO;
  if (typeText.includes('audio') || typeText.includes('voice') || typeText.includes('sound') || AUDIO_EXTENSIONS.has(extension)) return WANX_VIDEO_MEDIA_TYPES.REFERENCE_AUDIO;
  return WANX_VIDEO_MEDIA_TYPES.REFERENCE_IMAGE;
};

export const assertWanxVideoMediaSelection = (rawMedia, mode, duration = 5) => {
  const media = normalizeWanxVideoMedia(rawMedia);
  const counts = media.reduce((map, item) => map.set(item.type, (map.get(item.type) || 0) + 1), new Map());
  const frameItems = media.filter(item => FRAME_TYPES.has(item.type));
  const referenceItems = media.filter(item => REFERENCE_TYPES.has(item.type));
  const fileItems = media.filter(item => item.type === WANX_VIDEO_MEDIA_TYPES.FILE);
  const linkItems = media.filter(item => item.type === WANX_VIDEO_MEDIA_TYPES.LINK);

  if ((counts.get(WANX_VIDEO_MEDIA_TYPES.FIRST_FRAME) || 0) > 1) throw new Error('首帧最多 1 张');
  if ((counts.get(WANX_VIDEO_MEDIA_TYPES.LAST_FRAME) || 0) > 1) throw new Error('尾帧最多 1 张');
  if ((counts.get(WANX_VIDEO_MEDIA_TYPES.REFERENCE_IMAGE) || 0) > 10) throw new Error('参考图最多 10 张');
  if ((counts.get(WANX_VIDEO_MEDIA_TYPES.REFERENCE_VIDEO) || 0) > 5) throw new Error('参考视频最多 5 段');
  if ((counts.get(WANX_VIDEO_MEDIA_TYPES.REFERENCE_AUDIO) || 0) > 5) throw new Error('参考音频最多 5 段');
  if (fileItems.length > 1) throw new Error('参考文件最多 1 个');
  if (linkItems.length > 1) throw new Error('网页链接最多 1 个');
  if (frameItems.length && (referenceItems.length || fileItems.length || linkItems.length)) {
    throw new Error('首尾帧不能和参考素材、文件或网页链接同时使用');
  }
  if (fileItems.length && linkItems.length) throw new Error('参考文件和网页链接不能同时使用');

  const knownVideoDuration = media
    .filter(item => item.type === WANX_VIDEO_MEDIA_TYPES.REFERENCE_VIDEO)
    .reduce((sum, item) => sum + (finiteNumber(item.duration) || 0), 0);
  const knownAudioDuration = media
    .filter(item => item.type === WANX_VIDEO_MEDIA_TYPES.REFERENCE_AUDIO)
    .reduce((sum, item) => sum + (finiteNumber(item.duration) || 0), 0);
  media.forEach(item => {
    if (![WANX_VIDEO_MEDIA_TYPES.REFERENCE_VIDEO, WANX_VIDEO_MEDIA_TYPES.REFERENCE_AUDIO].includes(item.type)) return;
    const seconds = finiteNumber(item.duration);
    if (seconds != null && (seconds < 1 || seconds > 15)) {
      throw new Error(`${item.type === WANX_VIDEO_MEDIA_TYPES.REFERENCE_VIDEO ? '参考视频' : '参考音频'}单段时长需在 1-15 秒之间`);
    }
  });
  if (knownVideoDuration > 15) throw new Error('参考视频总时长不能超过 15 秒');
  if (knownAudioDuration > 15) throw new Error('参考音频总时长不能超过 15 秒');

  if (mode === 'text2video' && media.length) throw new Error('文生视频模式不能携带媒体素材');
  if (mode === 'img2video') {
    if ((counts.get(WANX_VIDEO_MEDIA_TYPES.FIRST_FRAME) || 0) !== 1 || media.length !== 1) {
      throw new Error('首帧生视频需要且只能使用 1 张首帧图片');
    }
  }
  if (mode === 'start-end') {
    if ((counts.get(WANX_VIDEO_MEDIA_TYPES.FIRST_FRAME) || 0) !== 1
      || (counts.get(WANX_VIDEO_MEDIA_TYPES.LAST_FRAME) || 0) !== 1
      || media.length !== 2) {
      throw new Error('首尾帧生视频需要各 1 张首帧和尾帧图片');
    }
  }
  if (mode === 'reference' && !(referenceItems.length || fileItems.length || linkItems.length)) {
    throw new Error('参考生视频请添加图片、视频、音频、文件或网页链接');
  }

  const normalizedDuration = normalizeWanxVideoDuration(duration);
  if (knownVideoDuration > 0 && normalizedDuration !== -1 && knownVideoDuration + normalizedDuration > 30) {
    throw new Error(`参考视频已占 ${knownVideoDuration.toFixed(1)} 秒，输出时长最多 ${Math.floor(30 - knownVideoDuration)} 秒`);
  }
  return media;
};

export const validateWanxVideoRequest = ({ model, prompt, media, mode, duration, resolution, ratio, seed }) => {
  const normalizedPrompt = String(prompt || '').trim();
  if (!normalizedPrompt && (!Array.isArray(media) || media.length === 0)) {
    throw new Error('提示词和媒体素材至少填写一项');
  }
  if (normalizedPrompt.length > 20000) throw new Error('提示词不能超过 20000 个字符');
  const normalizedDuration = normalizeWanxVideoDuration(duration);
  return {
    model: normalizeWanxVideoModel(model),
    prompt: normalizedPrompt,
    media: assertWanxVideoMediaSelection(media, mode, normalizedDuration),
    duration: normalizedDuration,
    resolution: normalizeWanxVideoResolution(resolution),
    ratio: normalizeWanxVideoRatio(ratio),
    seed: normalizeWanxVideoSeed(seed),
  };
};

export const getWanxVideoMediaLabel = (item, media = []) => {
  const type = item?.type;
  if (type === WANX_VIDEO_MEDIA_TYPES.FIRST_FRAME) return '首帧';
  if (type === WANX_VIDEO_MEDIA_TYPES.LAST_FRAME) return '尾帧';
  const sameType = normalizeWanxVideoMedia(media).filter(candidate => candidate.type === type);
  const index = Math.max(0, sameType.findIndex(candidate => candidate.id === item?.id || candidate.url === item?.url));
  if (type === WANX_VIDEO_MEDIA_TYPES.REFERENCE_IMAGE) return `@图片${index + 1}`;
  if (type === WANX_VIDEO_MEDIA_TYPES.REFERENCE_VIDEO) return `@视频${index + 1}`;
  if (type === WANX_VIDEO_MEDIA_TYPES.REFERENCE_AUDIO) return `@音频${index + 1}`;
  if (type === WANX_VIDEO_MEDIA_TYPES.FILE) return '文件1';
  if (type === WANX_VIDEO_MEDIA_TYPES.LINK) return '网页1';
  return '素材';
};

export const resolveWanxVideoMediaMarkers = (text, rawMedia) => {
  if (!text) return '';
  const media = normalizeWanxVideoMedia(rawMedia);
  const resolveUrl = (url) => {
    const item = media.find(candidate => candidate.url === url);
    return item ? getWanxVideoMediaLabel(item, media) : '';
  };
  let result = String(text).replace(/\{\{ref:([^:}]*):([^:}]*):([^}]+)\}\}/g, (_, _category, _name, url) => resolveUrl(url));
  result = result.replace(/\{\{ref:([^:}]+):([^}]+)\}\}/g, (_, _name, url) => resolveUrl(url));
  return result.replace(/\s{2,}/g, ' ').trim();
};
